import {
  PickemEntryCancelledInsuredProperties,
  PickemEntryCreatedInsuredProperties,
  PickemEntryCreatedProperties,
  PickemEntryCreatedStandardProperties,
  PickemEntrySubmissionSharedProperties,
  PickemHigherLowerSelectionAddedProperties,
  PickemRivalsSelectionAddedProperties,
} from '@/ampli';
import { SelectedOverUnder } from '@/interfaces/constructed-interfaces/constructed-pick-em-over-under-appearance';
import { SelectedRival } from '@/interfaces/constructed-interfaces/constructed-pick-em-rival-appearance';
import { OverUnderLine } from '@/interfaces/over-under-line';
import { PayoutOutcome, PickLocation, SelectedOption } from '@/interfaces/pick-em';
import { PowerUpType } from '@/interfaces/power-ups';
import { SportId } from '@/interfaces/types';

import { toSnake } from '../helpers';
import langHelper from '../lang-helper';

interface AmpliBoost {
  id: string;
  boostType: 'none' | 'payout booster' | 'special' | 'sweepstakes' | 'discount';
}

interface SelectedLineTypes {
  selectedOverUnders: SelectedOverUnder[];
  selectedRivals: SelectedRival[];
}

interface NewSelection<T> {
  lobbyName?: SportId;
  totalNumberOfPicks?: number;
  pickLocation: PickLocation;
  selection: T;
}

interface PickEmEntrySlip {
  entryAmount: string;
  entrySlipId: string;
  payoutOutcome: PayoutOutcome;
  selectedOptions: SelectedOption[];
  selectedOverUnders: SelectedOverUnder[];
  selectedRivals: SelectedRival[];
  calculatedCoefficient?: number;
  correlationBlockersById?: { [id: string]: string[] };
}

interface SharedEventProperties {
  entry_amount: number;
  entry_id: string;
  number_of_picks_higher: number;
  number_of_picks_in_game: number;
  number_of_picks_lower: number;
  number_of_picks_rivals: number;
  number_of_picks_total: number;
  player_ids: string[];
  player_names: string[];
  sport_names: string[];
  shifted_payout_multiplier: number;
  has_shifted_payout: boolean;
  number_of_correlated_selections: number;
}

// note: we should try to migrate all helpers to their own modules/files
// (or try to co-locate them with the code that uses them)
// This makes it easier to isolate helper dependencies and not
// inadvertently introduce extraneous dependencies when importing
// only one helper function. Tree-shaking / dead code elimination
// can help with this to an extent, but in the interest of trying
// to keep our dependencies as lean as possible, it's good practice.
//
// That said... we can make the transition a little less impactful
// by re-exporting helpers from this file (if needed) until we're ready to
// remove this "barrel" file entirely.
// Net-new code can import individual helpers directly from their
// respective files.

// formats selected overunderlines for amplitude
export const formatSOULine = ({
  overUnderLine,
  lobbyName,
  selection,
  pickLocation,
  totalNumberOfPicks,
}: NewSelection<SelectedOverUnder> & {
  overUnderLine: OverUnderLine;
}): PickemHigherLowerSelectionAddedProperties => {
  const {
    constructedOverUnderAppearance: { player },
    option,
  } = selection;
  const currentOU = overUnderLine;
  const choice = option.choice as 'higher' | 'lower';
  const boost = getBoost(currentOU);
  const { displayStat, stat } = currentOU.overUnder.appearanceStat;

  return {
    adjustment_amount: null, // always null on OUs
    boost_type: boost.boostType,
    boost_id: boost.id,
    display_stat_name: displayStat,
    higher_lower: choice,
    is_alt: !currentOU.isMain,
    is_modified_projection: selection.option.payoutMultiplier !== 1,
    lobby_name: lobbyName,
    modifier_value: selection.option.payoutMultiplier,
    number_of_picks_total: totalNumberOfPicks,
    pick_display_choice: option.choiceDisplay,
    pick_location: pickLocation,
    pick_type: currentOU.liveEvent ? 'ingame' : 'pregame',
    player_id: player.id,
    player_name: langHelper.getPlayerFullName(player),
    projection_value: parseFloat(currentOU.statValue),
    sport_name: player.sportId,
    stat_name: stat,
  };
};

// formats selected rival lines for amplitude
export const formatSelectedRivalLine = ({
  lobbyName,
  selection,
  pickLocation,
}: NewSelection<SelectedRival>): PickemRivalsSelectionAddedProperties => {
  const {
    constructedPickEmRivalAppearance: { player },
    option,
  } = selection;

  const { appearanceStat } = option;

  return {
    adjustment_amount: parseFloat(option.spread),
    display_stat_name: appearanceStat.displayStat,
    lobby_name: lobbyName,
    pick_location: pickLocation,
    player_id: player.id,
    player_name: langHelper.getPlayerFullName(player),
    sport_name: player.sportId,
    stat_name: appearanceStat.stat,
  };
};

export const formatPickEmEntryCreatedProperties = ({
  correlationBlockersById,
  entryAmount,
  entrySlipId,
  isInsured,
  isPoolsEnabled,
  payoutOutcome,
  selectedOptions,
  selectedOverUnders,
  selectedRivals,
}: {
  correlationBlockersById: { [id: string]: string[] };
  entryAmount: string;
  entrySlipId: string;
  isInsured: boolean;
  isPoolsEnabled: boolean;
  payoutOutcome: PayoutOutcome;
  selectedOptions: SelectedOption[];
  selectedOverUnders: SelectedOverUnder[];
  selectedRivals: SelectedRival[];
}): PickemEntryCreatedProperties => {
  const sharedEventProperties = getSharedEventProperties({
    correlationBlockersById,
    entryAmount,
    entrySlipId,
    payoutOutcome,
    selectedOptions,
    selectedOverUnders,
    selectedRivals,
  });
  const boost = getBoostFromSOU(selectedOverUnders);
  const modifiedSelections = selectedOptions.filter(
    (option) => option.payoutMultiplier && option.payoutMultiplier !== 1
  );

  const insuranceOneLossMultiplier: number = parseFloat(
    payoutOutcome.insurance.fallbacks[0]?.payoutMultiplier
  );
  const parsedEntryAmount = parseFloat(entryAmount);

  return {
    ...sharedEventProperties,
    boost_id: boost.id,
    boost_type: ampliBoosts[boost.boostType],
    contains_modifier: modifiedSelections.length > 0,
    entry_type: isInsured ? 'flex' : 'standard',
    includes_pack: 'No', // Web doesn't support packs
    is_champions: isPoolsEnabled,
    number_of_modified_picks: modifiedSelections.length,
    payout_amount_standard: parsedEntryAmount * parseFloat(payoutOutcome.standard.payoutMultiplier),
    payout_multiplier_standard: parseFloat(payoutOutcome.standard.payoutMultiplier),
    ...(isInsured
      ? {
          payout_amount_insurance_max:
            parsedEntryAmount * parseFloat(payoutOutcome.insurance.maxMultiplier),
          payout_amount_insurance_with_loss: parsedEntryAmount * insuranceOneLossMultiplier,
          payout_multiplier_insurance_max: parseFloat(payoutOutcome.insurance.maxMultiplier),
          payout_multiplier_insurance_with_loss: insuranceOneLossMultiplier,
        }
      : {}),
  };
};

export const formatPickemEntrySlipStandard = (
  slip: Omit<PickEmEntrySlip, 'entrySlipId'> & Partial<PickEmEntrySlip>
): PickemEntryCreatedStandardProperties => {
  const { entryAmount, payoutOutcome, selectedOptions, selectedOverUnders } = slip;
  const boost = getBoostFromSOU(selectedOverUnders);
  const multiplier = payoutOutcome.standard.payoutMultiplier;
  const payoutInfo = {
    multiplier: parseFloat(multiplier),
    payoutAmount: (parseFloat(entryAmount) * parseFloat(multiplier)).toFixed(2),
  };
  const sharedEventProperties = getSharedEventProperties(slip);
  const modifiedSelections = selectedOptions.filter(
    (option) => option.payoutMultiplier && option.payoutMultiplier > 1
  );

  return {
    boost_id: boost.id,
    boost_type: boost.boostType,
    contains_modifier: modifiedSelections.length > 0,
    number_of_modified_picks: modifiedSelections.length,
    payout_amount_standard: parseFloat(payoutInfo.payoutAmount),
    payout_multiplier_standard: payoutInfo.multiplier,
    ...sharedEventProperties,
  };
};

// formats submitted insured pick'em entry slips
export const formatPickemEntrySlipInsured = (
  slip: Omit<PickEmEntrySlip, 'entrySlipId'> & Partial<PickEmEntrySlip>
): PickemEntryCreatedInsuredProperties => {
  const { entryAmount, payoutOutcome, selectedOptions } = slip;
  const sharedEventProperties = getSharedEventProperties(slip);
  const multiplier = payoutOutcome.insurance.payoutMultiplier;
  const payoutInfo = {
    multiplier: parseFloat(multiplier),
    payoutAmount: (parseFloat(entryAmount) * parseFloat(multiplier)).toFixed(2),
  };
  const oneLossFallback = payoutOutcome.insurance.fallbacks.find((fb) => fb.lossCount === 1);
  const payoutWithLossInfo = {
    multiplier: parseFloat(oneLossFallback?.payoutMultiplier),
    payoutAmount: (parseFloat(entryAmount) * parseFloat(oneLossFallback?.payoutMultiplier)).toFixed(
      2
    ),
  };
  const modifiedSelections = selectedOptions.filter(
    (option) => option.payoutMultiplier && option.payoutMultiplier > 1
  );

  return {
    contains_modifier: modifiedSelections.length > 0,
    number_of_modified_picks: modifiedSelections.length,
    payout_amount_insurance_max: parseFloat(payoutInfo.payoutAmount),
    payout_amount_insurance_with_loss: parseFloat(payoutWithLossInfo.payoutAmount),
    payout_multiplier_insurance_max: payoutInfo.multiplier,
    payout_multiplier_insurance_with_loss: payoutWithLossInfo.multiplier,
    ...sharedEventProperties,
  };
};

// formats outgoing shared entry slips
export const formatPickemEntryShareSubmission = ({
  entryAmount,
  entrySlipId,
  selectedOverUnders,
  selectedRivals,
}: {
  entryAmount: string;
  entrySlipId: string;
  selectedOverUnders: SelectedOverUnder[];
  selectedRivals: SelectedRival[];
}): PickemEntrySubmissionSharedProperties => {
  const playerDetails = getSLPlayerDetails({ selectedOverUnders, selectedRivals });
  const totalPicks = selectedOverUnders.length + selectedRivals.length;

  return {
    entry_amount: parseFloat(entryAmount),
    entry_id: entrySlipId,
    number_of_picks_total: totalPicks,
    player_ids: playerDetails.playerIds,
    player_names: playerDetails.playerNames,
    screen: 'confirm_entry',
    sport_names: playerDetails.sportNames,
  };
};

const ampliBoosts: { [key: string]: AmpliBoost['boostType'] } = {
  discount: 'discount',
  none: 'none',
  payoutBooster: 'payout booster',
  specialLine: 'special',
  sweepstakes: 'sweepstakes',
};

// retrieves boost from overunderline
export const getBoost = (oUL: OverUnderLine): AmpliBoost => {
  if (!oUL?.overUnder.boost) {
    return {
      id: null,
      boostType: 'none',
    };
  }

  const { boost } = oUL.overUnder;
  const ampliBoostType = ampliBoosts[boost.boostType];

  return {
    id: boost.id,
    boostType: ampliBoostType as AmpliBoost['boostType'],
  };
};

// retrieves boost from selected overunderlines
const getBoostFromSOU = (selectedOverUnders: SelectedOverUnder[]): AmpliBoost => {
  const boostedLine = selectedOverUnders.find((sOU) => sOU?.boost?.id);

  if (boostedLine) {
    return {
      id: boostedLine.boost.id,
      boostType: boostedLine.boost.boostType as AmpliBoost['boostType'],
    };
  }
  return {
    id: null,
    boostType: 'none',
  };
};

// gets the number of selected live lines on entry slip
const getInGameCount = ({ selectedOverUnders, selectedRivals }: SelectedLineTypes) => {
  const selected = [...selectedOverUnders, ...selectedRivals];

  let inGameCount = 0;
  selected.forEach((sLO) => {
    if ('constructedOverUnderAppearance' in sLO) {
      if (sLO.constructedOverUnderAppearance.hasLiveLines) inGameCount++;
    }
    if ('constructedPickEmRivalLine' in sLO) {
      if (sLO.constructedPickEmRivalLine.liveEvent) inGameCount++;
    }
  });

  return inGameCount;
};

// retrieves details for all players on entry slip
export const getSLPlayerDetails = ({ selectedOverUnders, selectedRivals }: SelectedLineTypes) => {
  const selected = [...selectedOverUnders, ...selectedRivals];

  const selectedPlayers = selected.map((sLO) => {
    if ('constructedOverUnderAppearance' in sLO) {
      const { constructedOverUnderAppearance } = sLO;
      return constructedOverUnderAppearance.player;
    }
    return sLO.constructedPickEmRivalAppearance.player;
  });

  return selectedPlayers.reduce(
    (acc, player) => {
      acc.playerIds.push(player.id);
      acc.playerNames.push(langHelper.getPlayerFullName(player));
      acc.sportNames.push(player.sportId);

      return acc;
    },
    { playerIds: [], playerNames: [], sportNames: [] }
  );
};

// properties shared between both creation events
const getSharedEventProperties = ({
  entryAmount,
  entrySlipId,
  selectedOptions,
  selectedOverUnders,
  selectedRivals,
  calculatedCoefficient,
  correlationBlockersById,
}: Omit<PickEmEntrySlip, 'entrySlipId'> & Partial<PickEmEntrySlip>): SharedEventProperties => {
  const higherCount = selectedOverUnders.filter((sOU) => sOU.option.choice === 'higher').length;
  const inGameCount = getInGameCount({ selectedOverUnders, selectedRivals });
  const lowerCount = selectedOverUnders.filter((sOU) => sOU.option.choice === 'lower').length;
  const playerDetails = getSLPlayerDetails({ selectedOverUnders, selectedRivals });

  return {
    entry_amount: parseFloat(entryAmount),
    ...(entrySlipId && { entry_id: entrySlipId }),
    number_of_picks_higher: higherCount,
    number_of_picks_in_game: inGameCount,
    number_of_picks_lower: lowerCount,
    number_of_picks_rivals: selectedRivals.length,
    number_of_picks_total: selectedOptions.length,
    player_ids: playerDetails.playerIds,
    player_names: playerDetails.playerNames,
    sport_names: playerDetails.sportNames,
    shifted_payout_multiplier: calculatedCoefficient,
    has_shifted_payout: calculatedCoefficient && calculatedCoefficient !== 1,
    number_of_correlated_selections: Object.keys(correlationBlockersById ?? {}).length,
  };
};

export const getPowerUpTokenType = (powerUpType: PowerUpType) => toSnake(powerUpType);

export const formatPickemEntryCancelledInsured = ({
  entryAmount,
  payoutOutcome,
  selectedOptions,
  selectedOverUnders,
  selectedRivals,
}: {
  entryAmount: string;
  payoutOutcome: PayoutOutcome;
  selectedOptions: SelectedOption[];
  selectedOverUnders: SelectedOverUnder[];
  selectedRivals: SelectedRival[];
}): PickemEntryCancelledInsuredProperties => {
  const higherCount = selectedOverUnders.filter((sOU) => sOU.option.choice === 'higher').length;
  const inGameCount = getInGameCount({ selectedOverUnders, selectedRivals });
  const lowerCount = selectedOverUnders.filter((sOU) => sOU.option.choice === 'lower').length;
  const maxMultiplier = parseFloat(payoutOutcome.insurance.maxMultiplier);
  const maxMultiplierOneLoss = parseFloat(payoutOutcome.insurance.fallbacks[0]?.payoutMultiplier);
  const modifiedSelections = selectedOptions.filter(
    (option) => option.payoutMultiplier && option.payoutMultiplier > 1
  );
  const parsedEntryAmount = parseFloat(entryAmount);
  const playerDetails = getSLPlayerDetails({ selectedOverUnders, selectedRivals });

  return {
    entry_amount: parsedEntryAmount,
    number_of_modified_picks: modifiedSelections.length,
    number_of_picks_higher: higherCount,
    number_of_picks_in_game: inGameCount,
    number_of_picks_lower: lowerCount,
    number_of_picks_rivals: selectedRivals.length,
    number_of_picks_total: selectedOverUnders.length + selectedRivals.length,
    payout_amount_insurance_max: maxMultiplier * parsedEntryAmount,
    payout_amount_insurance_with_loss: maxMultiplierOneLoss * parsedEntryAmount,
    payout_multiplier_insurance_max: maxMultiplier,
    payout_multiplier_insurance_with_loss: maxMultiplierOneLoss,
    player_ids: playerDetails.playerIds,
    player_names: playerDetails.playerNames,
    sport_names: playerDetails.sportNames,
  };
};
